import { resolveSpeakerRole, isJobTitleRole } from './speaker-roles';
import { DEFAULT_TASK_DEADLINE } from './task-deadline';
import { canonicalizeSpeakerDisplayName } from './speaker-display-names';

export type ProtocolParticipant = {
  name: string;
  role: string;
  minutes: string;
  speakerKey: string;
  badgeRole?: string;
};

export type ProtocolSpeech = {
  title: string;
  speakerKey: string;
  role: string;
  position: string;
  body: string;
};

export type ProtocolTask = {
  num: number;
  task: string;
  assignee: string;
  deadline: string;
  priority: string;
};

export type ProtocolDecision = {
  num: number;
  decision: string;
  timestamp: string;
};

export type ProtocolObservation = {
  num: number;
  observation: string;
  timestamp: string;
};

export type ConclusionGroupId = 'good' | 'attention' | 'improve' | 'other';

export type ConclusionGroup = {
  id: ConclusionGroupId;
  title: string;
  items: string[];
};

export type ProtocolNextStep = {
  assignee: string;
  action: string;
};

export type ParsedProtocol = {
  title: string;
  meta: Record<string, string>;
  participants: ProtocolParticipant[];
  agenda: string[];
  speeches: ProtocolSpeech[];
  decisions: ProtocolDecision[];
  observations: ProtocolObservation[];
  tasks: ProtocolTask[];
  conclusions: string;
  conclusionGroups: ConclusionGroup[];
  nextSteps: ProtocolNextStep[];
  signatures: { chair: string; secretary: string; date: string };
  rawSections: Record<string, string>;
  officialOrders: boolean;
};

function extractSpeakerKey(text: string): string {
  const m = text.match(/SPEAKER_\d+/i);
  return m ? m[0].toUpperCase() : '';
}

function extractRole(text: string): string {
  const resolved = resolveSpeakerRole(text);
  if (resolved.label !== 'Участник') return resolved.label;
  const lower = text.toLowerCase();
  if (lower.includes('руководител') || lower.includes('председател')) return 'Руководитель';
  if (lower.includes('секретар')) return 'Секретарь';
  if (lower.includes('директор')) return 'Директор филиала';
  if (lower.includes('ттк')) return 'Сотрудник ТТК';
  return 'Участник';
}

function cleanSignatureValue(raw: string, kind: 'chair' | 'secretary' | 'date' = 'chair'): string {
  let s = (raw || '')
    .replace(/\*/g, '')
    .replace(/\[Подпись\]\s*/gi, '')
    .replace(/^\/+|\/+$/g, '')
    .trim();
  if (kind === 'secretary' && /^профессиональный\s+секретар/i.test(s)) return '';
  if (kind === 'secretary' && /^секретарь\s+(ktzh|ктж|совещания)/i.test(s)) return '';
  return s;
}

function roleFromNameCell(nameCell: string): string {
  const slash = nameCell.indexOf('/');
  if (slash >= 0) {
    const title = nameCell.slice(slash + 1).trim();
    if (title) return extractRole(title) === 'Участник' && title.length <= 60 ? title : extractRole(title);
  }
  return extractRole(nameCell);
}

function parseMetaLine(line: string, meta: Record<string, string>) {
  const m = line.match(/^\*\*(.+?):\*\*\s*(.*)$/);
  if (m) meta[m[1].trim()] = m[2].trim();
}

function parseTableRows(lines: string[]): string[][] {
  const rows: string[][] = [];
  for (const line of lines) {
    if (!line.includes('|') || /^[\s|:-]+$/.test(line.replace(/\|/g, ''))) continue;
    const cells = line.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length);
    if (cells.length) rows.push(cells);
  }
  return rows;
}

const CONCLUSION_CATEGORIES: { pattern: RegExp; id: ConclusionGroupId; title: string }[] = [
  { pattern: /что\s+хорошо/i, id: 'good', title: 'Что хорошо' },
  { pattern: /требует\s+внимания/i, id: 'attention', title: 'Что требует внимания' },
  { pattern: /возможности\s+улучшения/i, id: 'improve', title: 'Возможности улучшения' },
];

function stripInlineTimestamps(text: string): string {
  return (text || '')
    .replace(/\s*\(\d{1,2}:\d{2}\)/g, '')
    .replace(/\s*\[\d{1,2}:\d{2}\]/g, '')
    .trim();
}

function splitSpeechPosition(body: string): { position: string; body: string } {
  const trimmed = stripInlineTimestamps(body);
  if (!trimmed) return { position: '', body: '' };

  const nl = trimmed.indexOf('\n');
  if (nl < 0) return { position: '', body: trimmed };

  const first = trimmed.slice(0, nl).trim();
  const rest = trimmed.slice(nl + 1).trim();
  const looksLikeSpeechStart = /^\S+\s+[А-ЯЁA-Z]\./.test(first) || first.length > 100;
  const looksLikePosition =
    !looksLikeSpeechStart
    && (
      isJobTitleRole(first)
      || /директор|транстелеком|ттк|коммерческ|сотрудник/i.test(first)
      || (!first.endsWith('.') && first.length <= 80)
    );

  if (looksLikePosition && rest) {
    return { position: first, body: rest };
  }
  return { position: '', body: trimmed };
}

function stripListMarker(line: string): string {
  return line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[-*•]\s*/, '')
    .replace(/^\*\*(.+?)\*\*:?\s*$/, '$1')
    .replace(/^\*\*(.+?):\*\*\s*/, '$1: ')
    .trim();
}

function isConclusionHeader(line: string): { id: ConclusionGroupId; title: string } | null {
  const cleaned = stripListMarker(line).replace(/:$/, '').trim();
  for (const cat of CONCLUSION_CATEGORIES) {
    if (cat.pattern.test(cleaned)) {
      return { id: cat.id, title: cat.title };
    }
  }
  return null;
}

function parseConclusionGroups(body: string): ConclusionGroup[] {
  if (!body?.trim()) return [];

  const bySubheading = body.split(/^###\s+/m).slice(1);
  if (bySubheading.length >= 2) {
    const groups: ConclusionGroup[] = [];
    for (const part of bySubheading) {
      const nl = part.indexOf('\n');
      const heading = nl >= 0 ? part.slice(0, nl).trim() : part.trim();
      const itemsBody = nl >= 0 ? part.slice(nl + 1).trim() : '';
      const header = isConclusionHeader(heading);
      const items = itemsBody.split('\n')
        .map(stripListMarker)
        .filter(item => item && !isConclusionHeader(item));
      if (items.length) {
        groups.push({
          id: header?.id || 'other',
          title: header?.title || heading,
          items,
        });
      }
    }
    if (groups.length) return groups;
  }

  const groups: ConclusionGroup[] = [];
  let current: ConclusionGroup | null = null;

  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const header = isConclusionHeader(line);
    if (header) {
      current = { id: header.id, title: header.title, items: [] };
      groups.push(current);
      continue;
    }

    const item = stripListMarker(line);
    if (!item || isConclusionHeader(item)) continue;

    if (!current) {
      current = { id: 'other', title: 'Выводы', items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }

  return groups.filter(g => g.items.length > 0);
}

function parseNextStepLine(line: string): ProtocolNextStep | null {
  const cleaned = stripListMarker(line);
  if (!cleaned) return null;

  const boldDash = cleaned.match(/^\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/);
  if (boldDash) {
    return { assignee: boldDash[1].trim(), action: boldDash[2].trim() };
  }

  const brokenBold = cleaned.match(/^\*([^*\n]+)\*\*\s*[—–-]\s*(.+)$/);
  if (brokenBold) {
    return { assignee: brokenBold[1].trim(), action: brokenBold[2].trim() };
  }

  const colonIdx = cleaned.indexOf(':');
  if (colonIdx > 0 && colonIdx < 60) {
    const assignee = cleaned.slice(0, colonIdx).replace(/\*\*/g, '').trim();
    const action = cleaned.slice(colonIdx + 1).trim();
    if (assignee && action && !/^(ответственный|assignee)$/i.test(assignee)) {
      return { assignee, action };
    }
  }

  return { assignee: '', action: cleaned };
}

function parseNextSteps(body: string): ProtocolNextStep[] {
  return body.split('\n')
    .map(stripListMarker)
    .filter(Boolean)
    .map(parseNextStepLine)
    .filter((s): s is ProtocolNextStep => Boolean(s?.action));
}

export function parseProtocolMarkdown(text: string): ParsedProtocol {
  const result: ParsedProtocol = {
    title: '',
    meta: {},
    participants: [],
    agenda: [],
    speeches: [],
    decisions: [],
    observations: [],
    tasks: [],
    conclusions: '',
    conclusionGroups: [],
    nextSteps: [],
    signatures: { chair: '', secretary: '', date: '' },
    rawSections: {},
    officialOrders: false,
  };

  if (!text?.trim()) return result;

  const clean = text.replace(/^\*+\s*$/gm, '').trim();
  const h1 = clean.match(/^#\s+(.+)/m);
  if (h1) result.title = h1[1].trim();

  clean.split('\n').forEach(line => parseMetaLine(line.trim(), result.meta));

  const sections = clean.split(/^##\s+/m).slice(1);
  for (const block of sections) {
    const nl = block.indexOf('\n');
    const heading = nl >= 0 ? block.slice(0, nl).trim() : block.trim();
    const body = nl >= 0 ? block.slice(nl + 1).trim() : '';
    result.rawSections[heading] = body;

    const h = heading.toLowerCase();

    if (h.includes('участник')) {
      const rows = parseTableRows(body.split('\n'));
      if (!rows.length) continue;

      const header = rows[0].map(c => c.toLowerCase().trim());
      const nameIdx = header.findIndex(c =>
        c.includes('фио') || c === 'имя' || c.includes('участник'),
      );
      const badgeIdx = header.findIndex(c => c.includes('роль') && !c.includes('должность'));
      const positionIdx = header.findIndex(c => c.includes('должность'));
      const timeIdx = header.findIndex(c => c.includes('время'));
      const hasHeader = nameIdx >= 0 || badgeIdx >= 0 || positionIdx >= 0;
      const dataRows = hasHeader ? rows.slice(1) : (rows.length > 1 ? rows.slice(1) : rows);

      result.participants = dataRows.map(row => {
        let nameCell: string;
        let roleCell: string;
        let positionCell: string;
        let timeCell: string;

        if (hasHeader && nameIdx >= 0) {
          nameCell = row[nameIdx] || '';
          roleCell = badgeIdx >= 0 ? (row[badgeIdx] || '') : '';
          positionCell = positionIdx >= 0 ? (row[positionIdx] || '') : '';
          timeCell = timeIdx >= 0 ? (row[timeIdx] || '—') : (row[row.length - 1] || '—');
        } else if (/^\d+$/.test((row[0] || '').trim()) && row.length >= 3) {
          nameCell = row[1] || '';
          roleCell = row[2] || '';
          positionCell = row[3] || '';
          timeCell = row[4] || row[row.length - 1] || '—';
        } else {
          nameCell = row[0] || '';
          roleCell = row[1] || '';
          positionCell = row[2] || '';
          timeCell = row[3] || row[row.length - 1] || '—';
        }

        const key = extractSpeakerKey(nameCell);
        const rawName = nameCell
          .replace(/\(SPEAKER_\d+\)/i, '')
          .replace(/^SPEAKER_\d+\s*\/\s*/i, '')
          .trim()
          || nameCell.replace(/SPEAKER_\d+/i, '').trim()
          || nameCell;
        const name = canonicalizeSpeakerDisplayName(rawName) || rawName;

        const position = positionCell
          || (roleCell && isJobTitleRole(roleCell) ? roleCell : '');

        return {
          name,
          role: position || roleFromNameCell(nameCell),
          minutes: timeCell,
          speakerKey: key,
          badgeRole: roleCell && !isJobTitleRole(roleCell) ? roleCell : '',
        };
      }).filter(p => p.name && !/^\d+$/.test(p.name.trim()));
    }

    if (h.includes('повестка')) {
      result.agenda = body.split('\n')
        .map(l => l.replace(/^\d+\.\s*/, '').trim())
        .filter(Boolean);
    }

    if (h.includes('выступлен') || h.includes('ход совещания')) {
      const parts = body.split(/^###\s+/m).slice(1);
      if (parts.length) {
        result.speeches = parts.map(part => {
          const i = part.indexOf('\n');
          const title = i >= 0 ? part.slice(0, i).trim() : part.trim();
          const rawBody = i >= 0 ? part.slice(i + 1).trim() : '';
          const { position, body: speechBody } = splitSpeechPosition(rawBody);
          const key = extractSpeakerKey(title);
          return {
            title: canonicalizeSpeakerDisplayName(
              title.replace(/\(SPEAKER_\d+\)/i, '').trim(),
            ) || title.trim(),
            speakerKey: key,
            role: position || roleFromNameCell(title),
            position,
            body: speechBody,
          };
        }).filter(s => s.body);
      } else {
        const boldParts = body.split(/^\*\*(.+?)\*\*:?\s*$/m).slice(1);
        for (let i = 0; i < boldParts.length - 1; i += 2) {
          const title = boldParts[i]?.trim() || '';
          const rawBody = boldParts[i + 1]?.trim() || '';
          const { position, body: speechBody } = splitSpeechPosition(rawBody);
          if (!title || !speechBody) continue;
          result.speeches.push({
            title: canonicalizeSpeakerDisplayName(
              title.replace(/\(SPEAKER_\d+\)/i, '').trim(),
            ) || title.trim(),
            speakerKey: extractSpeakerKey(title),
            role: position || roleFromNameCell(title),
            position,
            body: speechBody,
          });
        }
      }
    }

    if (h.includes('решения') || h.includes('задачи')) {
      const ordersBlock = body.match(
        /###\s*Решения\s+и\s+поручения\s*\n([\s\S]*?)(?=###|$)/i,
      );
      const decisionsBlock = body.match(
        /###\s*Принятые\s+решения\s*\n([\s\S]*?)(?=###\s*(?:Наблюдения|Задачи)|$)/i,
      );
      const observationsBlock = body.match(
        /###\s*Наблюдения\s+и\s+риски\s*\n([\s\S]*?)(?=###\s*Задачи\s+и\s+поручения|$)/i,
      );
      const tasksBlock = body.match(/###\s*Задачи\s+и\s+поручения\s*\n([\s\S]*?)(?=###|$)/i);

      const parseDecisions = (block: string) => {
        const rows = parseTableRows(block.split('\n'));
        const dataRows = rows.filter(r => r.length >= 2 && !/^№?$/i.test(r[0]));
        const startIdx = dataRows[0]?.[0] === '№' || dataRows[0]?.[1]?.includes('Решение') ? 1 : 0;
        return dataRows.slice(startIdx).map((row, idx) => ({
          num: parseInt(row[0], 10) || idx + 1,
          decision: row[1] || row[0] || '',
          timestamp: row[2] || '—',
        })).filter(d => d.decision && d.decision.toLowerCase() !== 'решение');
      };

      const parseObservations = (block: string) => {
        const rows = parseTableRows(block.split('\n'));
        const dataRows = rows.filter(r => r.length >= 2 && !/^№?$/i.test(r[0]));
        const startIdx = dataRows[0]?.[0] === '№' || dataRows[0]?.[1]?.includes('Наблюдение') ? 1 : 0;
        return dataRows.slice(startIdx).map((row, idx) => ({
          num: parseInt(row[0], 10) || idx + 1,
          observation: row[1] || row[0] || '',
          timestamp: row[2] || '—',
        })).filter(o => o.observation && o.observation.toLowerCase() !== 'наблюдение');
      };

      const parseTasks = (block: string, taskHeader = 'задача') => {
        const rows = parseTableRows(block.split('\n'));
        const dataRows = rows.filter(r => r.length >= 3 && !/^№?$/i.test(r[0]));
        const startIdx = dataRows[0]?.[0] === '№'
          || dataRows[0]?.[1]?.toLowerCase().includes(taskHeader)
          || dataRows[0]?.[1]?.toLowerCase().includes('поручение')
          ? 1
          : 0;
        return dataRows.slice(startIdx).map((row, idx) => ({
          num: parseInt(row[0], 10) || idx + 1,
          task: row[1] || row[0] || '',
          assignee: canonicalizeSpeakerDisplayName(row[2] || row[1] || '—') || row[2] || row[1] || '—',
          deadline: row[3] || DEFAULT_TASK_DEADLINE,
          priority: row[4] || row[row.length - 1] || 'Средний',
        })).filter(t => {
          const label = t.task.toLowerCase();
          return t.task && label !== 'задача' && label !== 'поручение';
        });
      };

      if (ordersBlock) {
        result.officialOrders = true;
        result.tasks = parseTasks(ordersBlock[1], 'поручение');
      } else {
        if (decisionsBlock) {
          result.decisions = parseDecisions(decisionsBlock[1]);
        }
        if (observationsBlock) {
          result.observations = parseObservations(observationsBlock[1]);
        }
        if (tasksBlock) {
          result.tasks = parseTasks(tasksBlock[1]);
        }

        if (!result.decisions.length && !result.tasks.length) {
          result.tasks = parseTasks(body);
        }
      }
    }

    if (h.includes('вывод')) {
      result.conclusions = body;
      result.conclusionGroups = parseConclusionGroups(body);
    }

    if (h.includes('следующие шаги')) {
      result.nextSteps = parseNextSteps(body);
    }

    if (h.includes('подписи')) {
      const chair = body.match(/Председатель[:\s*]*(.+)/i);
      const sec = body.match(/Секретар[ья][:\s*]*(.+)/i);
      const date = body.match(/Дата[:\s*]*(.+)/i);
      const cleanSig = (s: string, kind: 'chair' | 'secretary' | 'date' = 'chair') =>
        cleanSignatureValue(s, kind);
      result.signatures = {
        chair: cleanSig(chair?.[1] || '', 'chair'),
        secretary: cleanSig(sec?.[1] || '', 'secretary'),
        date: cleanSig(date?.[1] || '', 'date'),
      };
    }
  }

  return result;
}
